import { createPortal } from 'react-dom';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { colors } from 'bench-style-constants';

const TooltipWrapper = styled.div`
  display: inline-flex;
  position: relative;
`;

const TooltipContent = styled.div`
  ${({ isClosed }) => isClosed && 'display: none;'} position: absolute;
  border-radius: 8px;
  white-space: nowrap;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    height: 0;
    width: 0;
  }

  top: ${props => props.top}px;

  ${props =>
    props.size === 'small' &&
    css`
      background: ${colors.charcoal};
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
      color: ${colors.white};
      font-size: 12px;
      padding: 8px 12px;

      ${props.vPosition === 'top' &&
      css`
        ${props.alignment === 'left' &&
        css`
          left: ${props.left}px;
          transform: translateX(-18px) translateY(-100%) translateY(-8px);
        `}
        ${props.alignment === 'center' &&
        css`
          left: ${props.left}px;
          transform: translateX(-50%) translateY(-100%) translateY(-8px);
        `}
      ${props.alignment === 'right' &&
        css`
          right: ${props.right}px;
          transform: translateX(18px) translateY(-100%) translateY(-8px);
        `}
      `}

      ${props.vPosition === 'bottom' &&
      css`
        ${props.alignment === 'left' &&
        css`
          left: ${props.left}px;
          transform: translateX(-18px) translateY(8px);
        `}
        ${props.alignment === 'center' &&
        css`
          left: ${props.left}px;
          transform: translateX(-50%) translateY(8px);
        `}
      ${props.alignment === 'right' &&
        css`
          right: ${props.right}px;
          transform: translateX(18px) translateY(8px);
        `}
      `}

    ${props.vPosition === 'center' &&
      css`
        transform: translateY(-50%);
        left: ${props.left}px;
      `}

    &::after {
        ${props.vPosition === 'top' &&
        css`
          bottom: -4px;
          border-right: 7px solid transparent;
          border-top: 5px solid ${colors.charcoal};
          border-left: 7px solid transparent;
          filter: drop-shadow(0px 4px 2px rgba(0, 0, 0, 0.16));
        `}
        ${props.vPosition === 'bottom' &&
        css`
          top: -4px;
          border-right: 7px solid transparent;
          border-bottom: 5px solid ${colors.charcoal};
          border-left: 7px solid transparent;
          filter: drop-shadow(0px -3px 2px rgba(0, 0, 0, 0.1));
        `}
      ${props.vPosition === 'center' &&
        css`
          // Styling only available for hPosition: right
          border-bottom: 7px solid ${colors.charcoal};
          border-left: 7px solid transparent;
          border-right: 7px solid transparent;
          filter: drop-shadow(0px -3px 2px rgba(0, 0, 0, 0.1));
          left: -9px;
          top: calc(50% - 3.5px);
          transform: rotate(-90deg);
        `}

      ${props.alignment === 'left' &&
        css`
          left: 10px;
        `}
      ${props.alignment === 'center' &&
        css`
          left: calc(50% - 7px);
        `}
      ${props.alignment === 'right' &&
        css`
          right: 10px;
        `}
      }
    `} ${props =>
    props.size === 'large' &&
    css`
      padding: 12px 16px;
      background: ${colors.white};
      box-shadow: 0 1px 10px 0 rgba(0, 0, 0, 0.1), 0 4px 5px 0 rgba(0, 0, 0, 0.1),
        0 2px 4px -1px rgba(0, 0, 0, 0.16);

      ${props.vPosition === 'top' &&
      css`
        ${props.alignment === 'left' &&
        css`
          left: ${props.left}px;
          transform: translateX(-25px) translateY(-100%) translateY(-16px);
        `}
        ${props.alignment === 'center' &&
        css`
          left: ${props.left}px;
          transform: translateX(-50%) translateY(-100%) translateY(-16px);
        `}
      ${props.alignment === 'right' &&
        css`
          right: ${props.right}px;
          transform: translateX(25px) translateY(-100%) translateY(-16px);
        `}
      `}

      ${props.vPosition === 'bottom' &&
      css`
        ${props.alignment === 'left' &&
        css`
          left: ${props.left}px;
          transform: translateX(-25px) translateY(16px);
        `}
        ${props.alignment === 'center' &&
        css`
          left: ${props.left}px;
          transform: translateX(-50%) translateY(16px);
        `}
      ${props.alignment === 'right' &&
        css`
          right: ${props.right}px;
          transform: translateX(25px) translateY(16px);
        `}
      `}

    &::after {
        ${props.vPosition === 'top' &&
        css`
          bottom: -8px;
          border-right: 11px solid transparent;
          border-left: 11px solid transparent;
          border-top: 9px solid white;
          filter: drop-shadow(0px 4px 2px rgba(0, 0, 0, 0.16));
        `}
        ${props.vPosition === 'bottom' &&
        css`
          top: -8px;
          border-bottom: 9px solid white;
          border-left: 11px solid transparent;
          border-right: 11px solid transparent;
          filter: drop-shadow(0px -4px 3px rgba(0, 0, 0, 0.05));
        `}

      ${props.alignment === 'left' &&
        css`
          left: 15px;
        `}
      ${props.alignment === 'center' &&
        css`
          left: calc(50% - 10px);
        `}
      ${props.alignment === 'right' &&
        css`
          right: 15px;
        `}
      }
    `};
`;

let toolTipContainer;

const ensureTooltipRootIsInDOM = () => {
  if (toolTipContainer) return;

  toolTipContainer = document.createElement('div');
  document.body.appendChild(toolTipContainer);
};

/**
 * Tool tip component that is displayed when child component is hovered. This component
 * can be positioned using the vPosition and hPosition props.
 * @param {node} children - component that will display tool tip when hovered
 * @param {node} contentComponent - content displayed in tool tip
 * @param {string} size - sets the size of the tool tip
 * @param {string} vPosition - sets the vertical position of tool tip
 * @param {string} [hPosition] - sets the horizontal position of tool tip; default: center
 * @param {string} [contentAlignment] - sets alignment of the tool tip content; default: center
 */
export default class Tooltip extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      alignment: 'center',
      bottom: 0,
      center: 0,
      left: 0,
      right: 0,
      top: -9999,
    };
  }

  UNSAFE_componentWillMount() {
    ensureTooltipRootIsInDOM();
  }

  componentDidMount() {
    window.addEventListener('click', this.closeTooltip);
    window.addEventListener('resize', this.closeTooltip);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.closeTooltip);
    window.removeEventListener('resize', this.closeTooltip);
  }

  /**
   * Checks incoming props and sets the properties to be passed to the
   * TooltipContent component.
   * @param {object} hoveredElement - element that triggers tool tip render
   */
  setTooltipPosition(hoveredElement) {
    const { top, bottom, height, width, x } = hoveredElement;
    const { hPosition, vPosition, contentAlignment } = this.props;
    const properties = {};
    const center = x + width / 2;

    if (vPosition === 'top') {
      properties.top = top;
    }
    if (vPosition === 'bottom') {
      properties.top = bottom;
    }
    if (vPosition === 'center') {
      properties.top = top + height / 2;
      // Currently only hPosition:right is supported
      properties.alignment = '';
      properties.left = x + width + 8;
    }
    if (hPosition === 'left') {
      properties.left = center - width / 2;
      properties.alignment = 'left';
    }
    if (hPosition === 'right') {
      properties.right = document.body.offsetWidth - center - width / 2;
      properties.alignment = 'right';
    }

    // contentAlignment can only be set for horizontally centered content
    if (hPosition === 'center' && vPosition !== 'center') {
      if (contentAlignment === 'right') {
        properties.right = document.body.offsetWidth - center;
        properties.alignment = 'right';
      } else if (contentAlignment === 'center' || contentAlignment === 'left') {
        properties.left = center;
        properties.alignment = contentAlignment;
      } else {
        properties.left = center;
      }
    }

    this.setState(properties);
  }

  /**
   * Triggers opening of TooltipContent component.
   */
  openTooltip(e) {
    e.stopPropagation();
    const element = e.currentTarget;
    if (element !== null) {
      const rect = element.getBoundingClientRect();
      this.setTooltipPosition(rect);
    }
    this.setState({ isOpen: true });
  }

  closeTooltip = () => {
    this.setState({ isOpen: false });
  };

  render() {
    return (
      <Fragment>
        <TooltipWrapper
          onClick={e => this.openTooltip(e)}
          onMouseEnter={e => this.openTooltip(e)}
          onMouseLeave={this.closeTooltip}
        >
          {this.props.children}
        </TooltipWrapper>
        {createPortal(
          <TooltipContent
            isClosed={!this.state.isOpen}
            alignment={this.state.alignment}
            bottom={this.state.bottom}
            center={this.state.center}
            left={this.state.left}
            right={this.state.right}
            top={this.state.top}
            {...this.props}
          >
            {this.props.contentComponent}
          </TooltipContent>,
          toolTipContainer,
        )}
      </Fragment>
    );
  }
}

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  contentAlignment: PropTypes.string,
  contentComponent: PropTypes.node.isRequired,
  size: PropTypes.string,
  hPosition: PropTypes.string,
  vPosition: PropTypes.string,
};

Tooltip.defaultProps = {
  contentAlignment: '',
  size: 'small',
  vPosition: 'top',
  hPosition: 'center',
};
